/**
 * LRU cache of audio blob object-URLs keyed by recordId.
 *
 * The audio endpoint has no Range support, so every player mount costs a
 * full-file download. Switching back to a recently viewed record reuses the
 * blob instead of re-downloading it. The cache owns the object-URL
 * lifecycle: entries are revoked when evicted by the LRU, NOT when the
 * player unmounts — a URL handed out for the active record is always the
 * most recently used entry and therefore never the eviction victim.
 */

const MAX_ENTRIES = 3;

/** Give a just-unmounted WaveSurfer time to detach before the URL dies. */
const REVOKE_DELAY_MS = 500;

// Map preserves insertion order — re-inserting on access makes it an LRU.
const urlsByRecordId = new Map<string, string>();
const inflightByRecordId = new Map<string, Promise<string>>();
// Bumped by `clearAudioBlobCache`: a download that started before a clear
// must not put its blob back into the cache when it lands.
let generation = 0;

// The record whose URL is currently handed to a mounted player. Insertion
// order does NOT imply it is the newest entry: rapid record switches start
// concurrent downloads that resolve in arbitrary order, so a stale record's
// late completion can make the ACTIVE record the oldest insertion — evicting
// it would revoke a URL mid-playback (MediaElement "Format error").
let activeRecordId: string | null = null;

/** Pin the active record's URL against eviction (null = nothing pinned). */
export function setActiveAudioRecord(recordId: string | null): void {
  activeRecordId = recordId;
}

/** Cached blob URL for the record, refreshing its LRU recency. */
export function getCachedAudioUrl(recordId: string): string | undefined {
  const url = urlsByRecordId.get(recordId);
  if (url !== undefined) {
    urlsByRecordId.delete(recordId);
    urlsByRecordId.set(recordId, url);
  }
  return url;
}

/**
 * Resolve the record's blob URL: cache hit → instant; concurrent calls for
 * the same record share one download; otherwise fetch, cache, and evict the
 * least recently used entry over the cap.
 *
 * Rejections propagate to the caller (and are NOT cached — a retry refetches).
 */
/** The viewer's access changed while a recording was downloading: it was dropped. */
export class AudioAccessChangedError extends Error {
  constructor() {
    super('Audio dropped: the viewer’s access changed during the download');
    this.name = 'AudioAccessChangedError';
  }
}

export function loadAudioBlobUrl(
  recordId: string,
  fetchBlobUrl: () => Promise<string>
): Promise<string> {
  const cached = getCachedAudioUrl(recordId);
  if (cached !== undefined) {
    return Promise.resolve(cached);
  }

  const inflight = inflightByRecordId.get(recordId);
  if (inflight) {
    return inflight;
  }

  const startedIn = generation;
  const promise = fetchBlobUrl()
    .then((url) => {
      if (startedIn === generation) {
        urlsByRecordId.set(recordId, url);
        evictOverflow();
        return url;
      }
      // Fetched under an access that has since changed: no cache entry owns it
      // and no one was handed it yet, so it is released at once. The caller's
      // fetcher belongs to the old access (it may hold another organization's
      // client), so it is not run again — a caller still mounted asks afresh.
      URL.revokeObjectURL(url);
      throw new AudioAccessChangedError();
    })
    .finally(() => {
      // A clear may have replaced this download with a newer one for the same
      // record: only its own entry is removed.
      if (inflightByRecordId.get(recordId) === promise) {
        inflightByRecordId.delete(recordId);
      }
    });
  inflightByRecordId.set(recordId, promise);
  return promise;
}

function evictOverflow(): void {
  while (urlsByRecordId.size > MAX_ENTRIES) {
    // Oldest insertion that is NOT the active record — never revoke a URL a
    // mounted player is using.
    let victimId: string | undefined;
    for (const id of urlsByRecordId.keys()) {
      if (id !== activeRecordId) {
        victimId = id;
        break;
      }
    }
    if (victimId === undefined) {
      return;
    }
    const url = urlsByRecordId.get(victimId)!;
    urlsByRecordId.delete(victimId);
    setTimeout(() => URL.revokeObjectURL(url), REVOKE_DELAY_MS);
  }
}

/**
 * Forget every cached recording — the viewer's access changed (or the
 * organization/subject did), and audio fetched under the previous access must
 * not play from here (E1.5 PR 4b, Codex #1745). URLs are revoked after the
 * same grace delay as an eviction; a download already in flight is released
 * when it lands and its caller gets `AudioAccessChangedError`.
 */
export function clearAudioBlobCache(): void {
  generation += 1;
  for (const url of urlsByRecordId.values()) {
    setTimeout(() => URL.revokeObjectURL(url), REVOKE_DELAY_MS);
  }
  urlsByRecordId.clear();
  inflightByRecordId.clear();
  activeRecordId = null;
}

/** Test-only: drop all entries and in-flight downloads without revoking. */
export function resetAudioBlobCacheForTests(): void {
  urlsByRecordId.clear();
  inflightByRecordId.clear();
  activeRecordId = null;
}
